import {
  Box,
  ButtonBase,
  Collapse,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { useIntakeStyle } from '@/karehero-app/tell-us-once/intake/IntakeStyle';

import { Icon } from '@karehero/llama/components/atoms/Icon/Icon';
import { radius } from '@karehero/llama/theme/radius';

// A recording in the transcript, the way a messaging app shows one: play it,
// see how long it is. Where the live captions heard it, the words can be opened
// under it to read, but not corrected: the summary is where that happens.
//
// The bars are derived from the length rather than the audio: a real waveform
// needs the file, and a fake one that moves is worse than a still one.
const bars = (seconds: number) =>
  Array.from({ length: 28 }, (_, i) => {
    const wave = Math.sin(i * 1.7 + seconds) * Math.cos(i * 0.6);
    return 5 + Math.round(Math.abs(wave) * 13);
  });

// Minutes and seconds, so a minute in reads 1:00 rather than 0:60.
const clock = (seconds: number) =>
  `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;

const VoiceNote = ({
  seconds,
  tone = 'sent',
  transcript,
  transcriptShown = false,
  onToggleTranscript,
}: {
  seconds: number;
  /**
   * What they said, where the live captions heard it. A sent note gets a Show
   * text link under the audio that opens the words in the same bubble. Read
   * only: anything wrong in it is corrected in the summary, not here.
   */
  transcript?: string;
  /**
   * Whether the words show. Not the note's own: it is one setting for the
   * whole chat, the same one as live captions, so the chat holds it.
   */
  transcriptShown?: boolean;
  /** Show text or Hide text was tapped, on this note or any other. */
  onToggleTranscript?: () => void;
  /**
   * `sent` sits in the transcript, `draft` is the one still being reviewed, and
   * `failed` is one that did not leave the phone. FailedMessage supplies the
   * alert and the way to try again around it.
   *
   * `recording` is the one still being made: a microphone where the others have
   * a play button, because there is nothing to play yet, and the clock counts up
   * rather than saying how long it is.
   */
  tone?: 'sent' | 'draft' | 'failed' | 'recording';
}) => {
  const { reply, replyInk } = useIntakeStyle();
  const failed = tone === 'failed';
  const recording = tone === 'recording';
  const onCoral = tone === 'sent' || failed;
  const ink = failed
    ? 'custom.neutral.50'
    : onCoral
      ? replyInk
      : 'text.primary';

  const textShown = transcriptShown;
  const readable = Boolean(transcript) && tone === 'sent';

  return (
    <Box
      sx={{
        px: 1,
        py: 0.75,
        minWidth: 220,
        // A fixed cap rather than a share of the chat: a share of a wrapper
        // that sizes itself to the note (the reading spinner's) wraps oddly.
        maxWidth: 280,
        bgcolor: failed ? 'error.main' : onCoral ? reply : 'background.paper',
        border: onCoral ? 0 : 1,
        borderColor: 'custom.neutral.200',
        borderRadius: `${radius.lg}px`,
        borderBottomRightRadius: onCoral ? '4px' : `${radius.lg}px`,
      }}
    >
      <Stack sx={{ alignItems: 'center' }} direction='row' spacing={1}>
        {recording ? (
          // Not a button: it is the state, not something to press. The way to
          // stop sits outside, where the send button does.
          <Box sx={{ display: 'flex', px: 0.5, color: 'custom.coral.600' }}>
            <Icon icon={'microphone' as never} fontSize={14} />
          </Box>
        ) : (
          <IconButton aria-label='Play' size='small' sx={{ color: ink }}>
            <Icon icon={'play' as never} fontSize={14} />
          </IconButton>
        )}

        <Stack
          sx={{ flex: 1, alignItems: 'center', height: 24 }}
          direction='row'
          spacing='2px'
        >
          {bars(seconds).map((h, i) => (
            <Box
              key={i}
              sx={{
                width: 2,
                height: h,
                borderRadius: '1px',
                bgcolor: ink,
                opacity: onCoral ? 0.8 : 0.4,
              }}
            />
          ))}
        </Stack>

        <Typography sx={{ pr: 0.5, color: ink }} variant='caption'>
          {clock(seconds)}
        </Typography>
      </Stack>

      {readable && (
        // Under the audio and in the same bubble: the words belong to this
        // note, so they open where it is rather than somewhere else.
        // Spacing is the token scale: 2 is 12px under the audio, 1 is 4px
        // round the link.
        <Box sx={{ px: 1 }}>
          <Collapse in={textShown}>
            <Typography sx={{ pt: 2, color: ink }} variant='body2'>
              {transcript}
            </Typography>
          </Collapse>
          <ButtonBase
            onClick={onToggleTranscript}
            aria-expanded={textShown}
            // Right, under the clock, as a quiet aside to the note.
            sx={{ py: 1, display: 'flex', width: 'fit-content', ml: 'auto' }}
          >
            <Typography
              sx={{ color: ink, textDecoration: 'underline' }}
              variant='caption'
            >
              {textShown ? 'Hide text' : 'Show text'}
            </Typography>
          </ButtonBase>
        </Box>
      )}
    </Box>
  );
};

export default VoiceNote;
